<template>
  <div>
    <slot :new-value="newValue" name="header" />

    <div class="tw-flex tw-gap-2">
      <div class="tw-flex tw-flex-col tw-flex-1">
        <AppRangeInput
          :id="id"
          v-model="offsetSeconds"
          :value-label="newValueHumanReadable"
          :min="min"
          :max="max"
          :step="step"
          :ticks="ticks"
          class="tw-pb-9"
        >
          <template #header>
            <div class="tw-flex tw-justify-between tw-text-xs tw-px-1 tw-mb-1" role="presentation">
              <span>{{ formatter.format(addSeconds(parsedValue, min)) }}</span>
              <span>{{ formatter.format(addSeconds(parsedValue, max)) }}</span>
              <div class="range-bound-ticks tw-absolute tw-inset-x-0 tw-border-x tw-h-full" />
            </div>
          </template>
          <template #tick="{ tick }">
            <button
              class="tw-relative tw-group tw-text-indigo-500 tw-text-xl tw-select-none"
              @click="offsetSeconds = tick.value"
            >
              <icon-system-uicons-warning-circle />
              <span
                class="tw-absolute tw-top-full tw-left-1/2 -tw-translate-x-1/2 tw-block tw-invisible tw-transition group-hover:tw-opacity-100 group-hover:tw-visible group-hover:-tw-translate-y-2 tw-pt-3 tw-opacity-0 tw-z-20"
              >
                <span
                  class="tw-block tw-p-2 tw-bg-white tw-text-sm tw-text-black/75 tw-shadow-lg tw-rounded"
                >
                  {{ tick.label }}
                  <br />
                  {{ formatter.format(parseISO(tick.timestamp)) }}
                </span>
              </span>
            </button>
          </template>
        </AppRangeInput>
      </div>
    </div>

    <slot :new-value="newValue" name="footer" />
  </div>
</template>

<script lang="ts">
let seqNumber = 0
const formatter = new Intl.DateTimeFormat(navigator.language, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 1,
})
</script>

<script lang="ts" setup>
import { useCached } from '@vueuse/core'
import { parseISO } from 'date-fns'
import { computed, ref, watch, watchEffect } from 'vue'
import { RecordingEvent } from '../types'
import AppRangeInput from './AppRangeInput.vue'
import { addSeconds, differenceInSeconds } from './util'

const props = withDefaults(
  defineProps<{
    modelValue: string
    events: RecordingEvent[]
    id?: string
    step?: number
    paddingSeconds?: number
  }>(),
  {
    paddingSeconds: 5,
    step: 1,
    id: `recording-time-input-${seqNumber++}`,
  },
)
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const parsedValue = computed(() => parseISO(props.modelValue))
const minDate = computed(
  () => [props.events.at(0)?.timestamp ?? props.modelValue, props.modelValue].sort()[0],
)
const maxDate = computed(
  () => [props.events.at(-1)?.timestamp ?? props.modelValue, props.modelValue].sort()[1],
)
const min = computed(() =>
  differenceInSeconds(
    addSeconds(parseISO(minDate.value), props.paddingSeconds * -1),
    parsedValue.value,
  ),
)
const max = computed(() =>
  differenceInSeconds(addSeconds(parseISO(maxDate.value), props.paddingSeconds), parsedValue.value),
)
const offsetSeconds = ref(differenceInSeconds(parsedValue.value, parsedValue.value))
const newValue = computed(() => addSeconds(parsedValue.value, offsetSeconds.value))
const newValueISO = computed(() => newValue.value.toISOString())
const newValueISOCached = useCached(newValueISO)
const newValueHumanReadable = computed(() => formatter.format(newValue.value))
const ticks = computed(() => {
  return props.events.map((event, index) => {
    const value = differenceInSeconds(parseISO(event.timestamp), parsedValue.value)
    return {
      label: event.name,
      value,
      timestamp: event.timestamp,
      id: `${props.id}-tick-${index}`,
    }
  })
})

watchEffect(() => {
  if (props.modelValue) {
    offsetSeconds.value = 0
  }
})

watch(newValueISOCached, (value) => {
  emit('update:modelValue', value)
})
</script>
